/**
 * Single source of truth for the sellable TruWe catalog.
 *
 * Two facades consume this module:
 *  - GEO feed        → /ai/products.json (rupees, marketing shape)
 *  - UCP catalog     → /ucp/v1/catalog/* (paise, spec shape)
 *
 * Never create a third product list. Amounts here are minor units (paise).
 */
import { SOLD_OUT, PREORDER_ACTIVE, isComingSoon } from "@/lib/storeStatus";
import { getPackPricingTable, type PackQty } from "@/lib/checkout-pricing";
import { getFallbackProduct } from "@/lib/fallbackProducts";

export const UCP_BASE = "https://truwe.in";
export const UCP_CURRENCY = "INR";
export const UCP_REGION = "IN";
export const FREE_SHIPPING_THRESHOLD_PAISE = 0;

export type CatalogTier = {
  /** Protocol length in days (30 / 60 / 90). */
  days: number;
  /** Selling price in paise. */
  pricePaise: number;
  /** MRP in paise. */
  mrpPaise: number;
  perDayApprox?: string;
};

export type CatalogSku = {
  handle: string;
  title: string;
  summary: string;
  keyIngredients: string[];
  benefits: string[];
  warnings: string[];
  /** Natural-language search terms that should resolve to this SKU. */
  keywords: string[];
  tiers: CatalogTier[];
};

const DAYS_BY_PACK: Record<PackQty, number> = { 1: 30, 2: 60, 3: 90 };

/**
 * Tiers are derived from the shared pricing registry (code defaults plus the
 * prices published from Admin → Products), so no price is written here.
 */
function tiersFor(handle: string, perDay: Partial<Record<number, string>> = {}): CatalogTier[] {
  const table = getPackPricingTable(handle);
  if (!table) return [];
  return ([1, 2, 3] as PackQty[]).map((qty) => {
    const days = DAYS_BY_PACK[qty];
    const label = perDay[days];
    return {
      days,
      pricePaise: table[qty].price * 100,
      mrpPaise: table[qty].mrp * 100,
      ...(label ? { perDayApprox: label } : {}),
    };
  });
}

export const CATALOG: CatalogSku[] = [
  {
    handle: "mag-absorb-pro",
    title: "Mag Absorb Pro",
    summary:
      "5-form magnesium matrix (liposomal bisglycinate, Aquamin®, chloride, aspartate, gluconate) with Zn+Cu, P-5-P (B6) and D3 cofactors, plus Bio-Absorb Matrix delivery support. Once-daily evening tablet for sleep, calm and recovery.",
    keyIngredients: [
      "Liposomal Magnesium Bisglycinate 200 mg",
      "Aquamin® Marine Magnesium 100 mg",
      "Magnesium Chloride 60 mg",
      "Magnesium Aspartate 50 mg",
      "Magnesium Gluconate 30 mg",
      "Zinc + Copper Bisglycinate 12.5 mg",
      "P-5-P (active B6) 3 mg",
      "Vitamin D3 600 IU",
      "Bio-Absorb Matrix 30 mg",
    ],
    benefits: ["sleep quality", "muscle relaxation", "stress + calm", "recovery"],
    warnings: [
      "Not a substitute for medical treatment.",
      "Consult a clinician if pregnant, lactating, or on prescription medication.",
    ],
    keywords: [
      "magnesium",
      "mag",
      "sleep",
      "insomnia",
      "glycinate",
      "bisglycinate",
      "aquamin",
      "cramps",
      "muscle",
      "recovery",
      "stress",
      "calm",
      "anxiety",
    ],
    get tiers(): CatalogTier[] {
      return tiersFor("mag-absorb-pro", { 30: "~₹45/day", 60: "~₹43/day", 90: "~₹38/day" });
    },
  },
  {
    handle: "liver-biome-plus",
    title: "Liver Biome Plus",
    summary:
      "Liposomal liver detox + 10B CFU multi-strain probiotic for the gut-liver axis. Supports Phase I and Phase II liver detoxification. Once-daily with breakfast.",
    keyIngredients: [
      "NAC 600 mg",
      "Liposomal Milk Thistle (Silymarin eq.) 350 mg",
      "Liposomal Curcumin 100 mg",
      "Liposomal Alpha-Lipoic Acid 100 mg",
      "Multi-strain probiotic 10B CFU",
      "Bio-Absorb Matrix 30 mg",
    ],
    benefits: ["liver detox support", "gut-liver axis", "antioxidant support"],
    warnings: [
      "Not for use during active gallbladder disease without clinician guidance.",
      "Consult a clinician if pregnant, lactating, or on prescription medication.",
    ],
    keywords: [
      "liver",
      "fatty liver",
      "nafld",
      "detox",
      "nac",
      "milk thistle",
      "silymarin",
      "curcumin",
      "alpha lipoic",
      "probiotic",
      "gut",
      "bloating",
    ],
    get tiers(): CatalogTier[] {
      return tiersFor("liver-biome-plus");
    },
  },
  {
    handle: "ova-balance",
    title: "Ova Balance",
    summary:
      "Myo + D-Chiro Inositol at the research-backed 40:1 ratio (2050 mg total) with Berberine HCl, Quatrefolic® L-5-MTHF, Vitamin D3 600 IU (15 mcg), magnesium, zinc and chromium. Once-daily sachet for PCOS, ovulation and cycle regularity.",
    keyIngredients: [
      "Myo-Inositol 2000 mg",
      "D-Chiro Inositol 50 mg (40:1 ratio)",
      "Berberine HCl 300 mg",
      "Quatrefolic® L-5-MTHF 400 mcg",
      "Vitamin D3 600 IU (15 mcg)",
      "Magnesium Bisglycinate 100 mg",
      "Zinc Bisglycinate 10 mg",
      "Chromium Picolinate 200 mcg",
      "Bio-Absorb Matrix 30 mg",
    ],
    benefits: ["PCOS support", "cycle regularity", "ovulation", "insulin sensitivity"],
    warnings: [
      "Not for use during pregnancy without clinician guidance.",
      "Berberine may interact with certain medications, consult a clinician.",
    ],
    keywords: [
      "pcos",
      "inositol",
      "myo",
      "d-chiro",
      "40:1",
      "berberine",
      "period",
      "cycle",
      "ovulation",
      "hormone",
      "fertility",
      "folate",
    ],
    get tiers(): CatalogTier[] {
      return tiersFor("ova-balance", { 30: "~₹60/sachet", 60: "~₹55/sachet", 90: "~₹49/sachet" });
    },
  },
];

export type UcpItem = {
  id: string;
  title: string;
  price: number;
  currency: string;
  availability: "InStock" | "OutOfStock" | "PreOrder";
  canonical_url: string;
  image_url: string;
  attributes: {
    protocol_days: number;
    sku_handle: string;
    region: string;
    mrp: number;
  };
};

/** Live availability. One flag drives PDP, GEO feed and UCP catalog. */
export function availability(handle?: string): "InStock" | "OutOfStock" | "PreOrder" {
  if (SOLD_OUT) return "OutOfStock";
  if (handle && isComingSoon(handle)) return "PreOrder";
  return PREORDER_ACTIVE ? "PreOrder" : "InStock";
}

/** Absolute hero image for a SKU, resolved from the same product source the PDP uses. */
export function imageUrlFor(handle: string): string {
  const raw = getFallbackProduct(handle)?.images.edges[0]?.node.url ?? "";
  if (!raw) return `${UCP_BASE}/products/${handle}`;
  return raw.startsWith("http") ? raw : `${UCP_BASE}${raw.startsWith("/") ? "" : "/"}${raw}`;
}

export function toUcpItem(sku: CatalogSku, t: CatalogTier): UcpItem {
  return {
    id: `${sku.handle}:${t.days}`,
    title: `${sku.title} · ${t.days}-day protocol`,
    price: t.pricePaise,
    currency: UCP_CURRENCY,
    availability: availability(sku.handle),
    canonical_url: `${UCP_BASE}/products/${sku.handle}`,
    image_url: imageUrlFor(sku.handle),
    attributes: {
      protocol_days: t.days,
      sku_handle: sku.handle,
      region: UCP_REGION,
      mrp: t.mrpPaise,
    },
  };
}

export function allItems(): UcpItem[] {
  return CATALOG.flatMap((s) => s.tiers.map((t) => toUcpItem(s, t)));
}

export function findSku(handle: string): CatalogSku | null {
  return CATALOG.find((s) => s.handle === handle) ?? null;
}

/** Parse a UCP item id like `mag-absorb-pro:30`. */
export function lookupItem(id: string): { sku: CatalogSku; tier: CatalogTier; item: UcpItem } | null {
  const [handle, daysRaw] = id.split(":");
  const sku = handle ? findSku(handle) : null;
  if (!sku) return null;
  const days = Number(daysRaw ?? 30);
  const tier = sku.tiers.find((t) => t.days === days);
  if (!tier) return null;
  return { sku, tier, item: toUcpItem(sku, tier) };
}

const KIDS_TERMS = ["kid", "kids", "child", "children", "gummy", "gummies"];

export type SearchResult = {
  items: UcpItem[];
  /** Set when we have no sellable SKU but a human-browsable page exists. */
  continue_url?: string;
  note?: string;
};

/** Natural-language search → sellable variants. */
export function searchCatalog(query: string, limit = 20): SearchResult {
  const q = query.trim().toLowerCase();
  if (!q) return { items: allItems().slice(0, limit) };

  const tokens = q.split(/[^a-z0-9:+.]+/i).filter(Boolean);

  if (tokens.some((t) => KIDS_TERMS.includes(t))) {
    return {
      items: [],
      continue_url: `${UCP_BASE}/kids`,
      note: "TruWe Kids is not yet sellable through UCP. Direct the human to the kids page.",
    };
  }

  const scored = CATALOG.map((sku) => {
    const haystack = [sku.title, sku.summary, ...sku.keyIngredients, ...sku.benefits, ...sku.keywords]
      .join(" ")
      .toLowerCase();
    let score = 0;
    for (const t of tokens) if (haystack.includes(t)) score += 1;
    for (const k of sku.keywords) if (q.includes(k)) score += 2;
    return { sku, score };
  })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);

  const items = scored.flatMap(({ sku }) => sku.tiers.map((t) => toUcpItem(sku, t))).slice(0, limit);
  if (items.length === 0) {
    return { items: [], continue_url: `${UCP_BASE}/shop`, note: "No matching protocol. Browse the full range." };
  }
  return { items };
}
